import {Temporal} from "temporal-polyfill";
import {ZonedDateTimeSerialisert} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {nil} from "felles-ng2/typer/nullish";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {erNil} from "../støttefunksjoner/nilsjekk";
import {erZonedDateTimeTidligereEnn, lesZonedDateTimeSerialisert, lesZonedDateTimeSerialisertOpt, skrivZonedDateTimeSerialisert, skrivZonedDateTimeSerialisertOpt} from "../støttefunksjoner/temporal";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {dateSomZonedDateTime} from "felles-ng2/støttefunksjoner/temporalTilDateKonverteringer";
import ZonedDateTime = Temporal.ZonedDateTime;
import ZonedDateTimeLike = Temporal.ZonedDateTimeLike;

export interface ZonedDateTimePeriodeSerialisert<SLUTT extends ZonedDateTimeSerialisert | nil> {
    start: ZonedDateTimeSerialisert;
    slutt: SLUTT;
}

export interface ZonedDateTimePeriodeLukketSerialisert extends ZonedDateTimePeriodeSerialisert<ZonedDateTimeSerialisert> {
}

export interface ZonedDateTimePeriodeMuligÅpenSluttSerialisert extends ZonedDateTimePeriodeSerialisert<ZonedDateTimeSerialisert | nil> {
}

export abstract class ZonedDateTimePeriode<SLUTT extends ZonedDateTime | nil> {

    readonly start: ZonedDateTime;
    readonly slutt: SLUTT;

    constructor(start: ZonedDateTime, slutt: SLUTT) {
        this.start = start;
        this.slutt = slutt;
    }

    inneholder(dato: ZonedDateTimeLike): boolean {
        if (ZonedDateTime.compare(this.start, dato) > 0) return false;
        if (this.harLukketSlutt() && ZonedDateTime.compare(this.slutt!, dato) < 0) return false;
        return true;
    }

    inneholderNå(klokke: Klokke): boolean {
        return this.inneholder(klokke.nåSomZonedDateTime());
    }

    starterFør(dato: ZonedDateTimeLike): boolean {
        return ZonedDateTime.compare(this.start, dato) < 0;
    }

    starterEtter(dato: ZonedDateTimeLike): boolean {
        return ZonedDateTime.compare(this.start, dato) > 0;
    }

    slutterFør(dato: ZonedDateTimeLike): boolean {
        if (this.harÅpenSlutt()) return false;
        return ZonedDateTime.compare(this.slutt!, dato) < 0;
    }

    /** Sann dersom periodens slutt er passert */
    erPassert(klokke: Klokke): boolean {
        return this.slutterFør(klokke.nåSomZonedDateTime());
    }

    overlapper(annen: ZonedDateTimePeriode<any>): boolean {
        return this.inneholder(annen.start) || annen.inneholder(this.start);
    }

    harÅpenSlutt(): boolean {
        return erNil(this.slutt);
    }

    harLukketSlutt(): boolean {
        return !this.harÅpenSlutt();
    }

    /** Sammenlikner denne perioden men angitt periode, returnerer negativt tall dersom denne er tidligere enn annen
     * (sammenlikner start, dersom start er lik sammenliknes slutt),
     * @return 0 dersom periodene er like (samme start og slutt),
     *   negativt tall dersom denne er tidligere enn annen,
     *   positivt tall dersom denne er senere enn annen, eller annen er nil */
    sammenliknMed(annen: ZonedDateTimePeriode<any> | nil) {
        if (erNil(annen)) return 1;

        let resultat = this.start.epochMilliseconds - annen.start.epochMilliseconds;
        if (resultat != 0) return resultat;

        if (this.harÅpenSlutt() && annen.harÅpenSlutt()) return 0;
        if (this.harÅpenSlutt()) return 1;
        if (annen.harÅpenSlutt()) return -1;

        return this.slutt!.epochMilliseconds - annen.slutt!.epochMilliseconds;
    }

    abstract iTidssone(tidssone: Tidssoneinformasjon): ZonedDateTimePeriode<SLUTT>;

    abstract somSerialiserbar(): ZonedDateTimePeriodeSerialisert<any>;
}

/** Periode der både start og slutt er angitt  */
export class ZonedDateTimePeriodeLukket extends ZonedDateTimePeriode<ZonedDateTime> {
    /** Deserialiserer til ZonedDateTimePeriodeLukket fra json-representasjon sendt fra server */
    static les(periode: ZonedDateTimePeriodeLukketSerialisert, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriodeLukket {
        return new ZonedDateTimePeriodeLukket(lesZonedDateTimeSerialisert(periode.start, tidssone), lesZonedDateTimeSerialisert(periode.slutt!, tidssone));
    }

    /** Deserialiserer til ZonedDateTimePeriodeLukket fra json-representasjon sendt fra server */
    static lesOpt(periode: ZonedDateTimePeriodeLukketSerialisert | nil, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriodeLukket | null {
        if (erNil(periode)) return null;
        return ZonedDateTimePeriodeLukket.les(periode, tidssone);
    }

    /** Oppretter ZonedDateTimePeriodeLukket fra periode med Date typen  */
    static fraDate(periode: {
        start: Date,
        slutt: Date
    }, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriodeLukket {
        return new ZonedDateTimePeriodeLukket(dateSomZonedDateTime(periode.start, tidssone), dateSomZonedDateTime(periode.slutt!, tidssone));
    }

    constructor(start: ZonedDateTime, slutt: ZonedDateTime) {
        super(start, slutt);
    }

    /** Returnerer måneder perioden spenner over.
     * Dvs (antall 'månedsskift' man passerer mellom start og slutt) + 1.
     * Merk at beregningen gjøres ihht den tidssone som er angitt i start. */
    antallMåneder(): number {
        const justertStart = this.start.toPlainDate().with({day: 1});
        var justertSlutt = this.slutt.withTimeZone(this.start.getTimeZone()).toPlainDate().with({day: this.slutt.daysInMonth});
        const varighet = justertStart.until(justertSlutt);
        if (varighet.blank) return 1;
        return Math.floor(varighet.total({unit: 'months', relativeTo: justertStart})) + 1;
    }

    /** Returnerer antall kalenderdager i perioden.
     * Dvs (antall 'midnatt' man passerer mellom start og slutt) + 1.
     * Merk at beregningen gjøres ihht den tidssone som er angitt i start. */
    antallDager(): number {
        return this.start.toPlainDate()
          .until(this.slutt.withTimeZone(this.start.getTimeZone()).toPlainDate())
          .total({unit: 'days'}) + 1;
    }

    antallMinutter(): number {
        return this.start
          .until(this.slutt)
          .total({unit: 'minutes'});
    }

    erTidligereEllerLik(annen: ZonedDateTimePeriodeLukket): boolean {
        return this.sammenliknMed(annen) <= 0;
    }

    erTidligereEnn(annen: ZonedDateTimePeriodeLukket) {
        return this.sammenliknMed(annen) < 0;
    }

    /**
     * @deprecated bruk !sluttErEtterStart() istedenfor
     */
    startErEtterSlutt(): boolean {
        return erZonedDateTimeTidligereEnn(this.slutt, this.start);
    }

    /** vanlig validering er at denne må være true for at perioden er gyldig. Dersom denne er false, så vil det ikke være mulig å sende perioden til server.
     * MøteDato.java kan kun opprettes med start < slutt
     */
    sluttErEtterStart(): boolean {
        return erZonedDateTimeTidligereEnn(this.start, this.slutt);
    }

   /* /!** Sammenlikner denne perioden men angitt periode, returnerer negativt tall dersom denne er tidligere enn annen
     * (sammenlikner start, dersom start er lik sammenliknes slutt),
     * 0 dersom periodene er like (samme start og slutt), positivt tall dersom denne er senere enn annen. *!/
    sammenliknMed(annen: ZonedDateTimePeriodeLukket) {
        let resultat = this.start.epochMilliseconds - annen.start.epochMilliseconds;
        if (resultat != 0) return resultat;
        return this.slutt.epochMilliseconds - annen.slutt.epochMilliseconds;
    }*/

    iTidssone(tidssone: Tidssoneinformasjon): ZonedDateTimePeriodeLukket {
        return new ZonedDateTimePeriodeLukket(this.start.withTimeZone(tidssone.somZoneId()), this.slutt.withTimeZone(tidssone.somZoneId()));
    }

    somSerialiserbar(): ZonedDateTimePeriodeLukketSerialisert {
        return {start: skrivZonedDateTimeSerialisert(this.start), slutt: skrivZonedDateTimeSerialisert(this.slutt)}
    }
}

/** Periode der slutt kan være null. Dersom slutt er null er perioden 'evigvarende' */
export class ZonedDateTimePeriodeMuligÅpenSlutt extends ZonedDateTimePeriode<ZonedDateTime | null> {

    /** Deserialiserer til ZonedDateTimePeriodeMuligÅpenSlutt fra json-representasjon sendt fra server */
    static les(periode: ZonedDateTimePeriodeMuligÅpenSluttSerialisert, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriodeMuligÅpenSlutt {
        return new ZonedDateTimePeriodeMuligÅpenSlutt(lesZonedDateTimeSerialisert(periode.start, tidssone), lesZonedDateTimeSerialisertOpt(periode.slutt, tidssone));
    }

    /** Deserialiserer til ZonedDateTimePeriodeMuligÅpenSlutt fra json-representasjon sendt fra server */
    static lesOpt(periode: ZonedDateTimePeriodeMuligÅpenSluttSerialisert | nil, tidssone: Tidssoneinformasjon = Tidssoneinformasjon.UTC): ZonedDateTimePeriodeMuligÅpenSlutt | null {
        if (erNil(periode)) return null;
        return ZonedDateTimePeriodeMuligÅpenSlutt.les(periode, tidssone);
    }

    constructor(start: ZonedDateTime, slutt: ZonedDateTime | null) {
        super(start, slutt);
    }

    iTidssone(tidssone: Tidssoneinformasjon): ZonedDateTimePeriodeMuligÅpenSlutt {
        return new ZonedDateTimePeriodeMuligÅpenSlutt(this.start.withTimeZone(tidssone.somZoneId()), this.slutt?.withTimeZone(tidssone.somZoneId()) || null);
    }

    somSerialiserbar(): ZonedDateTimePeriodeMuligÅpenSluttSerialisert {
        return {start: skrivZonedDateTimeSerialisert(this.start), slutt: skrivZonedDateTimeSerialisertOpt(this.slutt)}
    }
}

/** Sammenlikner angitte perioder, returnerer negativt tall dersom a er tidligere enn b
 * (sammenlikner start, dersom start er lik sammenliknes slutt),
 * @return 0 dersom periodene er like (samme start og slutt),
 *  negativt tall dersom a.start er tidligere enn b, eller a er nil, eller a.start=b.start men a.slutt er tidligere enn b.slutt
 *  positivt tall dersom a.start er senere enn b, eller b er nil, eller a.start=b.start men a.slutt er senere enn b.slutt */
export function sammenliknZonedDateTimePeriode(a: ZonedDateTimePeriode<any> | nil, b: ZonedDateTimePeriode<any> | nil): number {
    if (erNil(a) && erNil(b)) return 0;
    if (erNil(a)) return -1;
    return a.sammenliknMed(b);
}
